import AppDownloadWrapper from '@/components/AppDownloadWrapper'
import ComponentMetricsBoundary from '@/components/ComponentMetricsBoundary/ComponentMetricsBoundary'
import EmailSignUp from '@/components/EmailSignUp'
import useBootstrapData from '@/hooks/useBootstrapData'
import { isICollectionType } from '@/shared-utils/content-model-type-guards'
import isLoadedInAppView from '@/shared-utils/isLoadedInAppView'
import {
  ContentfulRichText,
  type IArticleType,
  type ICollectionType,
  useContentfulWithPredicate,
} from '@pcln/contentful-components'
import { useFeature } from '@pcln/feature-components'
import { SilhouetteCard } from '@pcln/marketing'
import { BenefitsTable, Faqs, Hero, PropValue, Quotes, ZigZag } from '@pcln/vip'
import { useRouter } from 'next/router'
import { Box, Container, Relative, Text } from 'pcln-design-system'
import { type ReactNode } from 'react'
import styled from 'styled-components'
import data from './data'
import Intro from './Intro'
import MemberCapture from './MemberCapture'
import NavBar from './NavBar'
import { getUrlForAuthPage, getVipFamilyUrl } from './utils'

const ContentfulRichTextStyled = styled(ContentfulRichText)`
  margin: 0px;
`
type Faq = {
  question: string
  answer: ReactNode
}

function isCollectionOfIArticleType(content: unknown): content is ICollectionType<IArticleType> {
  return isICollectionType(content) && content.items.every((item) => item.contentTypeId === 'article')
}

export default function LandingPage() {
  const FAQHeadlineFallback = 'Frequently Asked Questions'

  const articles = useContentfulWithPredicate('articles', isCollectionOfIArticleType)
  const faqs = articles?.items.reduce<ReadonlyArray<Faq>>((array, item) => {
    if (item.body && item.headline) {
      return [
        ...array,
        {
          question: item.headline,
          answer: <ContentfulRichTextStyled body={item.body} />,
        },
      ]
    }
    return array
  }, [])

  const { query } = useRouter()
  const isPageInAppView = isLoadedInAppView(query)
  const { isMobile } = useBootstrapData()
  const isVipPlatinum20 = useFeature('siteExperience.isVipPlatinum20') ?? false

  return (
    <Box>
      <Box color='background.light'>
        <Hero imageSrc={data.imageSrc} content={data.heroContent} />
        <Relative mt={[-6, null, 0]}>
          <NavBar
            categories={
              isPageInAppView ? Array.from(data.categories).slice(0, -1) : Array.from(data.categories)
            }
          />
          <Intro
            onButtonClick={() => {
              window.location.href = getUrlForAuthPage(isPageInAppView)
            }}
          />
        </Relative>
        <Box px={[2, null, null, null, 5]} pt={4} mx={[2, null, null, null, 5]}>
          <BenefitsTable id='benefits-table' isVipPlatinum20={isVipPlatinum20} />
        </Box>
        <Box px={3} pt={[5, null, null, null, 3]} mt='-160px'>
          <Text textAlign='center' fontSize={0} color='text.light'>
            *Trips are considered complete one day after the trip concludes
          </Text>
        </Box>
        <MemberCapture
          onButtonClick={() => {
            window.location.href = getUrlForAuthPage(isPageInAppView)
          }}
        />
        <PropValue {...data.valueProp} />
      </Box>
      <Box id='vip-family' size='xl' mt={[3, null, null, null, 5]} mx={[3, null, null, null, 6]}>
        <SilhouetteCard
          labelText='family'
          title='Unlock bigger savings 5x faster with a VIP Family account'
          firstTextLine={data.firstTextLine}
          lastTextLine={data.lastTextLine}
          benefitsList={data.familyBenefitsList}
          callToActionText='Create your VIP family'
          onClick={() => {
            window.location.href = getVipFamilyUrl()
          }}
        />
      </Box>
      <Box id='overview' p={3} color='background.lightest'>
        <ZigZag
          onClick={() => {
            window.location.href = getUrlForAuthPage(isPageInAppView)
          }}
          {...data.zigzag}
        />
        <Box mx={-3} py={5}>
          <Quotes showInCarouselMode maxWidth={1086} contents={data.quotes} />
        </Box>
        <Text id='faq' mb={5} fontSize={5} textAlign='center' color='primary'>
          {articles?.headline || FAQHeadlineFallback}
        </Text>
        <Box px={[2, null, null, null, 5]}>
          <Faqs faqs={faqs || data.faqsFallback} />
        </Box>
      </Box>
      <Container size='xl' mb={5}>
        <ComponentMetricsBoundary componentId='EmailSignUp'>
          <EmailSignUp appCode={isMobile ? 'MOBILEWEB' : 'DESKTOP'} sourceId='LANDING_VIP' />
        </ComponentMetricsBoundary>

        {isPageInAppView ? null : (
          <Box id='app-download'>
            <AppDownloadWrapper />
          </Box>
        )}
      </Container>
    </Box>
  )
}
